import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { AccountsService } from '../../accounts.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PaymentProposal, PaymentProposalGenerateRequest } from '../phase7-ap-controls.model';

@Component({
    selector: 'app-payment-proposals',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule, ButtonModule, CheckboxModule, DatePickerModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TagModule, ToastModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageFormSectionComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './payment-proposals.component.html',
    styleUrls: ['./payment-proposals.component.css']
})
export class PaymentProposalsComponent implements OnInit {
    companyId = 0;
    loading = false;
    proposals: PaymentProposal[] = [];
    request: PaymentProposalGenerateRequest = {};
    selectedStatus?: string;

    statusOptions = ['DRAFT', 'APPROVED', 'RELEASED', 'VOUCHERS_GENERATED', 'REMITTANCE_GENERATED', 'CANCELLED'].map(value => ({ label: value.replaceAll('_', ' '), value }));
    currencyOptions = ['SAR', 'USD', 'EUR', 'AED'].map(value => ({ label: value, value }));
    priorityOptions = ['LOW', 'NORMAL', 'HIGH', 'URGENT'].map(value => ({ label: value, value }));

    constructor(
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        this.request = {
            owningCompanyId: this.companyId,
            runDate: new Date(),
            dueDateCutoff: new Date(),
            includePaymentHeld: false,
            actorId: this.actorId()
        };
        this.load();
    }

    get metrics(): PageMetric[] {
        const draft = this.proposals.filter(p => p.status === 'DRAFT').length;
        const approved = this.proposals.filter(p => p.status === 'APPROVED').length;
        const released = this.proposals.filter(p => p.status === 'RELEASED').length;
        const total = this.proposals.reduce((sum, p) => sum + Number(p.totalProposedPayment || 0), 0);
        return [
            { label: 'Draft', value: draft, tone: 'info' },
            { label: 'Approved', value: approved, tone: 'success' },
            { label: 'Released', value: released, tone: 'warn' },
            { label: 'Proposal Value', value: this.money(total), tone: 'success' }
        ];
    }

    load(): void {
        if (!this.companyId) return;
        this.loading = true;
        this.accountsService.searchPaymentProposals(this.companyId, this.selectedStatus ? [this.selectedStatus] : undefined).subscribe({
            next: proposals => {
                this.proposals = proposals ?? [];
                this.loading = false;
            },
            error: err => this.fail('Proposal search failed', err)
        });
    }

    generate(): void {
        this.request.owningCompanyId = this.companyId;
        this.request.actorId = this.actorId();
        this.accountsService.generatePaymentProposal(this.request).subscribe({
            next: proposal => {
                this.messageService.add({ severity: 'success', summary: 'Proposal generated', detail: `${proposal.proposalNumber || 'Payment proposal'} is ready for review.` });
                this.load();
                if (proposal.proposalId) this.open(proposal);
            },
            error: err => this.fail('Generate failed', err)
        });
    }

    approve(row: PaymentProposal): void {
        if (!row.proposalId) return;
        this.accountsService.approvePaymentProposal(row.proposalId, this.actorId(), 'Approved from proposal workbench').subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Proposal approved.' }); this.load(); },
            error: err => this.fail('Approval failed', err)
        });
    }

    release(row: PaymentProposal): void {
        if (!row.proposalId) return;
        this.accountsService.releasePaymentProposal(row.proposalId, this.actorId(), 'Released from proposal workbench').subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Released', detail: 'Proposal released.' }); this.load(); },
            error: err => this.fail('Release failed', err)
        });
    }

    vouchers(row: PaymentProposal): void {
        if (!row.proposalId) return;
        this.accountsService.generatePaymentProposalVouchers(row.proposalId).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Vouchers generated', detail: 'Approved proposal lines were converted to vouchers.' }); this.load(); },
            error: err => this.fail('Voucher generation failed', err)
        });
    }

    remittance(row: PaymentProposal): void {
        if (!row.proposalId) return;
        this.accountsService.generatePaymentProposalRemittance(row.proposalId, this.actorId()).subscribe({
            next: () => { this.messageService.add({ severity: 'success', summary: 'Remittance generated', detail: 'Remittance advice records created.' }); this.load(); },
            error: err => this.fail('Remittance failed', err)
        });
    }

    open(row: PaymentProposal): void {
        if (row.proposalId) this.router.navigate(['/accounts/enterprise/payables/payment-proposals/view', row.proposalId]);
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'APPROVED' || status === 'VOUCHERS_GENERATED' || status === 'REMITTANCE_GENERATED') return 'success';
        if (status === 'RELEASED') return 'warn';
        if (status === 'DRAFT') return 'info';
        if (status === 'CANCELLED') return 'danger';
        return 'secondary';
    }

    money(amount?: number, currency = 'SAR'): string {
        return `${currency} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    private fail(summary: string, err: any): void {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary, detail: err.error?.message || 'Operation failed.' });
    }

    private actorId(): number | undefined {
        const user = this.sessionStorageService.getUser();
        return user?.id ?? user?.userId ?? this.sessionStorageService.getCustomer()?.id;
    }
}
